import React from 'react';
import { Button, Form, Modal } from 'antd';
import { useTranslation } from 'react-i18next';
import { Formik } from 'formik';
import { MenuOutlined } from '@ant-design/icons';
import type { DragEndEvent } from '@dnd-kit/core';
import { DndContext } from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { arrayMove, verticalListSortingStrategy, SortableContext } from '@dnd-kit/sortable';
import FormCheckbox from '../../global/Forms/FormCheckbox';
import { DragHandle } from '../../global/dragableTable/DragHandle';
import { DragRow } from '../../global/dragableTable/DragRow';

type ColumnProps = {
  dataKey: string;
  label: string;
  visible: boolean;
};

type CustomizeLogModalProps = {
  showModal: boolean;
  columns: ColumnProps[];
  onCancel: () => void;
  handleSave: (colums: { dataKey: string; label: string; visible: boolean }[]) => void;
};

const CustomizeLogModal: React.FC<CustomizeLogModalProps> = props => {
  const { t } = useTranslation();

  return (
    <Modal
      open={props.showModal}
      onCancel={() => props.onCancel()}
      footer={null}
      closable={false}
      destroyOnClose
    >
      <Formik
        initialValues={{
          columns: props.columns,
        }}
        onSubmit={values => {
          props.handleSave(values.columns);
        }}
      >
        {({ handleSubmit, setFieldValue, dirty, isSubmitting, values }) => {
          const updateOptions = (columns: any) => {
            setFieldValue('columns', columns);
          };

          const onDragEnd = ({ active, over }: DragEndEvent) => {
            if (active.id !== over?.id) {
              const activeIndex = values.columns.findIndex(col => col.dataKey === active?.id);
              const overIndex = values.columns.findIndex(col => col.dataKey === over?.id);
              const sortedCols = arrayMove(values.columns, activeIndex, overIndex);
              updateOptions(sortedCols);
            }
          };

          return (
            <div>
              <div className="text-3xl mb-7">{t('importer:customizeColumns')}</div>
              <Form layout="vertical" className="import-export-logs__colum-order-form">
                <DndContext modifiers={[restrictToVerticalAxis]} onDragEnd={onDragEnd}>
                  <SortableContext
                    items={values.columns.map(i => i.dataKey)}
                    strategy={verticalListSortingStrategy}
                  >
                    {values.columns.map((col: any, index: number) => (
                      <DragRow
                        key={col.dataKey}
                        rowId={col.dataKey}
                        index={index}
                        classname="import-export__order-row"
                      >
                        <FormCheckbox className="mr-2" name={`columns[${index}].visible`} />
                        <span className="flex-1">{col.label}</span>
                        <DragHandle cursor="grab" icon={<MenuOutlined />} />
                      </DragRow>
                    ))}
                  </SortableContext>
                </DndContext>
              </Form>

              <div className="flex-col items-end mt-7">
                <div>
                  <Button onClick={() => props.onCancel()} className="drawer-submit__bottom-cancel">
                    {t('common:cancel')}
                  </Button>
                  <Button
                    type="primary"
                    onClick={() => handleSubmit()}
                    disabled={!dirty || isSubmitting}
                  >
                    {t('common:save')}
                  </Button>
                </div>
              </div>
            </div>
          );
        }}
      </Formik>
    </Modal>
  );
};

export default CustomizeLogModal;
